import React, { useContext, useEffect, lazy } from 'react';
import { Outlet } from 'react-router-dom';
import JourneyList from '@components/ui/JourneyList';
import AppContext from '@context';
import { useTimeout } from '@hooks/useTimeout';
import useLoadJourneys from '@hooks/useLoadJourneys';
import useLoadListWaitingConfirmDelivery from '@hooks/useLoadListWaitingConfirmDelivery';
import useLoadProposals from '@hooks/useLoadProposals';
import { ToastJourneyInterface } from '@components/ui/JourneyList/JourneyListType';
import { useQuery } from 'react-query';
import { getLatestNotificationProposal } from '@services/proposals';
import * as Styled from './JourneyManagementStyles';
import ModalMonitoring from '../ModalMonitoring';
import { JourneyManagementProps } from './JourneyManagementTypes';
import ModalListConfirmDelivery from '../ModalListConfirmDelivery';
import ModalUpdatedAddress from '../ModalUpdatedAddress';
import DialogUnrecognized from '../DialogUnrecognized';
import DialogCancelJourney from '../DialogCancelJourney';
import Notification from '../Notification';
import DrawerProposal from '../DrawerProposal';
import Header from './Header';
import ButtonCreateJourneyFixed from './ButtonCreateJourneyFixed';

const CompanyEvaluating = lazy(() => import('companyEvaluating/App'));

function JourneyManagement({ isMobile }: JourneyManagementProps) {
  const { fetchJourneys } = useLoadJourneys('lazy');
  const { fetchListConfirmDelivery } = useLoadListWaitingConfirmDelivery();
  const { fetchProposals } = useLoadProposals();
  const { dispatch } = useContext(AppContext);

  const addTimeout = useTimeout();

  function updateTabs() {
    fetchProposals({ page: 1 });
    handleFetchJourneys({});
  }

  function handleFetchJourneys({ page = 1 }: { page?: number }) {
    fetchJourneys({
      page,
    });
  }

  const refetchIntervalProposals = 600000; // Equivalente 10 minutos em milissegundo
  useQuery(
    'PROPOSALSLATESTNOTIFICATION',
    () => {
      return getLatestNotificationProposal().then((response) => {
        response?.forEach((notification, index) => {
          addTimeout(
            () => {
              window.dispatchEvent(
                new CustomEvent('notificationShow', {
                  detail: {
                    journeyId: '',
                    heading: notification.message,
                    severity: 'neutral',
                    notificationId: notification.id,
                    flow: 'truckAbandonmentNotice',
                  },
                }),
              );
            },
            index === 0 ? 0 : 9000,
          );
        });
      });
    },
    {
      retry: false,
      refetchIntervalInBackground: true,
      refetchInterval: refetchIntervalProposals,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    updateTabs();
  }, []);

  function handlerOpenModalConfirm(journeyId: string) {
    dispatch({
      type: 'ui_dialogDeliveryConfirmation_set_show',
      payload: { isShow: true, journeyId },
    });
  }
  function handlercloseModalConfirm() {
    dispatch({
      type: 'ui_dialogDeliveryConfirmation_set_show',
      payload: { isShow: false, journeyId: '' },
    });
  }

  function handleClickActionToast(
    action: ToastJourneyInterface['action'],
    journeyId?: string,
  ) {
    if (!action || !journeyId) {
      window.dispatchEvent(
        new CustomEvent('notificationShow', {
          detail: {
            isShow: true,
            heading: 'Não foi possível realizar esta ação',
            message: 'Tente novamente mais tarde',
            severity: 'error',
          },
        }),
      );
      return;
    }

    if (action.slug === 'monitoringJourney') {
      dispatch({
        type: 'ui_modalMonitoring_set_show',
        payload: {
          isShow: true,
          flow: 'toastProposalConfirm',
          journeyId: journeyId || '',
        },
      });
    }
  }

  useEffect(() => {
    fetchListConfirmDelivery();
  }, []);

  return (
    <Styled.Page>
      <Outlet />
      <Header isMobile={isMobile} />
      <Styled.WrapperContent>
        <JourneyList
          isMobile={isMobile}
          onErrorGetAgain={() => {
            handleFetchJourneys({});
          }}
          handlerOpenModalConfirm={(journey) =>
            handlerOpenModalConfirm(journey.id)
          }
          handlercloseModalConfirm={() => handlercloseModalConfirm()}
        />
      </Styled.WrapperContent>

      <ModalMonitoring
        isMobile={isMobile}
        handlerOpenModalConfirm={(journeyId) =>
          handlerOpenModalConfirm(journeyId)
        }
      />

      <ModalUpdatedAddress />
      <ModalListConfirmDelivery />

      <DialogUnrecognized
        isMobile={isMobile}
        onSuccess={() => handleFetchJourneys({})}
      />
      <DialogCancelJourney isMobile={isMobile} />
      <DrawerProposal isMobile={isMobile} />

      <React.Suspense fallback={<p>Carregando...</p>}>
        <CompanyEvaluating feature="createDenounce" />
      </React.Suspense>
      <React.Suspense fallback={<p>Carregando...</p>}>
        <CompanyEvaluating feature="evaluating" />
      </React.Suspense>

      <Notification
        onClickAction={(action, journeyId) =>
          handleClickActionToast(action, journeyId)
        }
      />

      <ButtonCreateJourneyFixed isMobile={isMobile} />
    </Styled.Page>
  );
}

export default JourneyManagement;
